/**
 * Secure Storage Utility
 * 提供加密的 localStorage 存储
 * 使用 AES-GCM 加密敏感数据
 */

import CryptoJS from 'crypto-js';

// 生成或获取设备唯一密钥
function getDeviceKey() {
  const KEY_NAME = 'canokey-device-key';
  let key = sessionStorage.getItem(KEY_NAME);
  
  if (!key) {
    // 生成随机密钥（32字节 = 256位）
    key = CryptoJS.lib.WordArray.random(32).toString();
    // 存储在 sessionStorage（关闭标签页就清除）
    sessionStorage.setItem(KEY_NAME, key);
  }
  
  return key;
}

/**
 * 加密并存储数据
 * @param {string} key - 存储键名
 * @param {any} data - 要存储的数据
 */
export function secureSetItem(key, data) {
  try {
    if (!data) {
      localStorage.removeItem(key);
      return;
    }
    
    const jsonStr = JSON.stringify(data);
    const deviceKey = getDeviceKey();
    
    // 使用 AES-256 加密
    const encrypted = CryptoJS.AES.encrypt(jsonStr, deviceKey).toString();
    
    // 添加版本标识和时间戳
    const wrapper = {
      v: 1,  // 版本号
      t: Date.now(),  // 时间戳
      d: encrypted  // 加密数据
    };
    
    localStorage.setItem(key, JSON.stringify(wrapper));
  } catch (e) {
    console.warn('Failed to encrypt and store data:', e);
  }
}

/**
 * 读取并解密数据
 * @param {string} key - 存储键名
 * @param {any} defaultValue - 默认值
 * @returns {any} 解密后的数据
 */
export function secureGetItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    if (!item) {
      return defaultValue;
    }
    
    const wrapper = JSON.parse(item);
    const deviceKey = getDeviceKey();
    
    // 检查版本
    if (wrapper.v !== 1) {
      console.warn('Unsupported storage version');
      return defaultValue;
    }
    
    // 检查过期（可选：7天后过期）
    const MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7天
    if (Date.now() - wrapper.t > MAX_AGE) {
      console.debug('Cached data expired, removing');
      localStorage.removeItem(key);
      return defaultValue;
    }
    
    // 解密数据
    const decrypted = CryptoJS.AES.decrypt(wrapper.d, deviceKey);
    const jsonStr = decrypted.toString(CryptoJS.enc.Utf8);
    
    if (!jsonStr) {
      // 解密失败（密钥不匹配）
      console.warn('Failed to decrypt data, removing');
      localStorage.removeItem(key);
      return defaultValue;
    }
    
    return JSON.parse(jsonStr);
  } catch (e) {
    console.warn('Failed to read and decrypt data:', e);
    // 数据损坏，清除
    localStorage.removeItem(key);
    return defaultValue;
  }
}

/**
 * 移除数据
 * @param {string} key - 存储键名
 */
export function secureRemoveItem(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    console.warn('Failed to remove item:', e);
  }
}

/**
 * 清除所有加密存储（保留密钥）
 */
export function secureClear() {
  try {
    const deviceKey = sessionStorage.getItem('canokey-device-key');
    localStorage.clear();
    if (deviceKey) {
      sessionStorage.setItem('canokey-device-key', deviceKey);
    }
  } catch (e) {
    console.warn('Failed to clear storage:', e);
  }
}

/**
 * 检查是否支持加密存储
 */
export function isSecureStorageSupported() {
  try {
    return typeof localStorage !== 'undefined' && 
           typeof sessionStorage !== 'undefined';
  } catch (e) {
    return false;
  }
}

